// Broker abstractions. The browser uses an in-tab SharedWorker broker;
// the same interface is meant to drop onto a Cloudflare Durable Object
// when we want cross-machine sessions. ControllerSocket talks to a
// Transport (transport.ts); the broker terminates that wire and runs
// the routing the C++ controller would otherwise run.

import type { Envelope, From, To } from '../controller_socket';

export type { Envelope, From, To };

/**
 * One client-side connection from the broker's perspective. The broker
 * does not care whether the connection is backed by a MessagePort, an
 * in-process EventTarget, or a Durable Object WebSocket — only that it
 * can deliver envelopes and learn when the peer closes.
 */
export interface BrokerConnection {
  readonly id: string;
  send(env: Envelope): void;
  /** Close from the broker's side. */
  close(code: number, reason: string): void;
}

/** Per-connection state the broker tracks once node.register lands. */
export interface ConnectionState {
  id: string;
  conn: BrokerConnection;
  registered: boolean;
  nodeId: string;
  role: From['role'];
  sessionId?: string;
  kind: string;
  // Topic patterns this connection subscribed to (deduped).
  subs: Set<string>;
  // Participant binding (set by participant.claim / participant.assign).
  participantId?: string;
  participantRoleId?: string;
  // Stable per-installation user identity. Replayed by the client on
  // every connect via `node.register.payload.userId`; if absent the
  // broker mints one and returns it in the register response so the
  // client can adopt + persist. Stamped onto every routed envelope's
  // `from.userId` so chat / markers / audit rows survive reconnects.
  userId?: string;
  // Operator-entered human label. Stamped onto `from.displayName`.
  displayName?: string;
}

/**
 * Persistence + export hooks. The default LocalStorageStore writes after
 * every routed envelope; a future DO-backed store can implement the same
 * surface with no broker changes.
 */
export interface BrokerStore {
  /** Replay anything we persisted earlier. May return null on first boot. */
  load(): Promise<BrokerSnapshot | null> | BrokerSnapshot | null;
  /** Persist current state after each route. Fire-and-forget. */
  save(snapshot: BrokerSnapshot): void;
  /**
   * Append a routed envelope to the event log. Separate from save() so
   * stores can choose between snapshot-only and append-only models.
   */
  appendEvent(row: EventLogRow): void;
  /**
   * Export a session's events as a JSON document. Returns the same
   * shape the controller's REST export emits so downstream tooling can
   * consume both.
   */
  exportSession(sessionId: string): SessionExport;
  /**
   * Optional resource-row hooks for stores that persist
   * `resource.*` writable tables (studies / presets). The in-browser
   * broker keeps these in memory and skips the hooks; the Durable
   * Object catalog backs them with SQL. Same signature as the
   * `resource.put` / `resource.delete` dispatch sees on the wire,
   * minus the protocol framing.
   */
  saveResource?(row: ResourceRow): void;
  deleteResource?(resource: string, id: string): void;
  /**
   * Replayed alongside `load()` on construction so the broker
   * repopulates its in-memory resource table from durable storage.
   * Synchronous or async; the broker handles both branches the same
   * way it does for `load()`.
   */
  loadResources?(): Promise<ResourceRow[]> | ResourceRow[];
}

/**
 * One row of a writable resource family (studies / presets), the unit
 * `resource.put` / `resource.delete` operate on. The wire-level body is
 * opaque; `rev` is the opaque revision token the protocol's `ifMatch`
 * / `ifNoneMatch` check against.
 */
export interface ResourceRow {
  resource: string;
  id: string;
  body: unknown;
  rev: string;
}

export interface BrokerSnapshot {
  version: 1;
  /** Last-known node rows (subset of NodeStatus). */
  nodes: NodeRow[];
  sessions: SessionRow[];
  participants: ParticipantRow[];
  /** Tail of the event log; full export comes from appendEvent stream. */
  events: EventLogRow[];
}

export interface NodeRow {
  nodeId: string;
  role: From['role'];
  kind: string;
  label?: string;
  state: 'connected' | 'disconnected';
  sessionId?: string;
  participantId?: string;
  lastSeen: string;
}

export interface SessionRow {
  sessionId: string;
  studyId: string;
  state: 'pending' | 'active' | 'paused' | 'stopped';
  startedAt?: string;
  stoppedAt?: string;
  participantRoles: Array<{
    participantRoleId: string;
    /** Role label authored at design time ("Pilot", "Co-pilot"). */
    displayName: string;
    /**
     * Optional per-session human assignment ("Alice"), set by the
     * facilitator on the Run card before session.start. When
     * present, the kiosk pre-fills the participant's displayName
     * with this value so chat / markers attribute correctly
     * without the participant having to enter a name. When absent,
     * the kiosk prompts after seat claim.
     */
    assignedName?: string;
  }>;
  // The verbatim session.start payload, kept for state.snapshot consumers
  // that want fields beyond the summary.
  payload?: Record<string, unknown>;
}

export interface ParticipantRow {
  sessionId: string;
  participantRoleId: string;
  state: 'open' | 'claimed' | 'assigned' | 'disconnected';
  participantId?: string;
  agentNodeId?: string;
}

export interface EventLogRow {
  id: number;
  sessionId: string;
  kind: string;
  /**
   * Sender's wall-clock at emit (ms since epoch). Copied verbatim
   * from `envelope.tAgent`. Replaces the legacy `tSession`
   * (session-relative seconds) — see PROTOCOL.md §12.
   */
  tAgent?: number;
  /**
   * Snapshot of the producing node's clock-skew at record time
   * (ms, signed). Frozen here so the canonical session-relative
   * coordinate `(tAgent + agentClockDeltaMs) - sessionStart` is a
   * pure per-row computation. Zero for envelopes whose producer is
   * not a daemon (browsers don't sync) or hasn't yet completed
   * the clock-sync handshake.
   */
  agentClockDeltaMs?: number;
  producer: string;
  role?: string;
  recordedAt: string;
  correlationId?: string;
  /** Envelope `key` (PROTOCOL.md §5a). Empty string for legacy rows only. */
  key: string;
  /** Retry counter; 0 on first publish, ≥1 on broker-recorded retries. */
  attempt: number;
  payload: unknown;
  to?: To;
}

export interface SessionExport {
  sessionId: string;
  session?: SessionRow;
  participants: ParticipantRow[];
  events: EventLogRow[];
  exportedAt: string;
}

/** Wall-clock indirection so tests can pin time. */
export interface Clock {
  now(): number;
  nowIso(): string;
}

export const systemClock: Clock = {
  now: () => Date.now(),
  nowIso: () => new Date().toISOString(),
};
